import {
  getDefaultSlots,
  getActPool,
  initNeeds,
  isWeekendDow,
  pickSlot,
  resolveAct,
  scoreFloat,
  simDow,
  tickNeeds,
} from '../simulation/lifeSimEngine.js';
import { getActivityPlaceKey } from '../simulation/activityConfig.js';

const PLACE_LABELS = {
  home: 'Home',
  restaurant: 'Restaurant',
  grocery: 'Grocery',
  gym: 'Gym',
  office: 'Office',
  cafe: 'Cafe',
  bar: 'Bar',
  recreation: 'Rec Center',
};

const MAX_BACKFILL_TRANSITIONS = 48;
const MAX_BACKFILL_ENTRIES = 20;
const MAX_CATCHUP_MINUTES = 96 * 60;

function hashString(input) {
  let hash = 2166136261;
  const text = String(input || '');
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createDeterministicRng(seedInput) {
  let state = hashString(seedInput) || 1;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function weightedPickWithRng(items, scoreFn, rng) {
  const scored = items.map((item) => ({ item, score: scoreFn(item) }));
  const total = scored.reduce((sum, entry) => sum + entry.score, 0);
  if (total <= 0) {
    return items[0] || null;
  }
  let roll = rng() * total;
  for (const { item, score } of scored) {
    roll -= score;
    if (roll <= 0) {
      return item;
    }
  }
  return items[items.length - 1] || null;
}

function inferCatchupStartTimestamp(activityLog, nowMs, lifeSim) {
  const latestLoggedAt = Array.isArray(activityLog)
    ? activityLog.reduce((latest, entry) => {
        const timestamp = Number(entry?.timestamp || 0);
        return Number.isFinite(timestamp) ? Math.max(latest, timestamp) : latest;
      }, 0)
    : 0;
  if (latestLoggedAt > 0) {
    return latestLoggedAt;
  }

  const simMin = Number(lifeSim?.sim_min);
  if (!Number.isFinite(simMin)) {
    return 0;
  }

  const now = new Date(nowMs);
  const currentMin = now.getHours() * 60 + now.getMinutes();
  const deltaMin = currentMin - simMin;
  if (deltaMin <= 0 || deltaMin > 12 * 60) {
    return 0;
  }
  return nowMs - deltaMin * 60000;
}

function normalizeLifeStateForDay(state) {
  while (state.simMin >= 1440) {
    state.simMin -= 1440;
    state.dayNum += 1;
    state.firedToday = new Set();
    state.actEndMin = Math.max(0, (state.actEndMin || 0) - 1440);
  }
  if (!Number.isFinite(state.actEndMin) || state.actEndMin < 0) {
    state.actEndMin = 0;
  }
}

function activityPlaceKey(activityId) {
  return getActivityPlaceKey(activityId) || 'home';
}

function inferCurrentPlaceKey(world, activityId) {
  const rawTag = typeof world?.location_tag === 'string' ? world.location_tag.trim() : '';
  if (rawTag) {
    const pieces = rawTag.split('_');
    if (pieces.length > 1) {
      return pieces[0];
    }
  }
  return activityId ? activityPlaceKey(activityId) : 'home';
}

function resolvePlaceName(agent, placeKey) {
  const explicitName = agent?.personalPlaces?.[placeKey]?.name;
  if (explicitName) {
    return explicitName;
  }

  const fallback = PLACE_LABELS[placeKey] || 'Destination';
  if (placeKey === 'home' && agent?.homeCity) {
    return `Home in ${agent.homeCity}`;
  }
  if (agent?.homeCity) {
    return `${fallback} near ${agent.homeCity}`;
  }
  return fallback;
}

function describeActivityPlace(agent, activityId) {
  const placeKey = activityPlaceKey(activityId);
  return {
    placeKey,
    locationTag: `${placeKey}_${activityId || 'idle'}`,
    placeName: resolvePlaceName(agent, placeKey),
  };
}

function buildHistoricEntryId(agentId, type, key, timestamp) {
  const safeKey = String(key || 'entry')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'entry';
  return `historic-${agentId}-${type}-${safeKey}-${Math.round(timestamp)}`;
}

function pickNextLifeActivity(agent, state, rng) {
  const weekend = isWeekendDow(simDow(state.dayNum));
  const slots = weekend
    ? (agent.schedule?.weekendSlots ?? getDefaultSlots(true))
    : (agent.schedule?.weekdaySlots ?? getDefaultSlots(false));
  const slotMatch = pickSlot(slots, state.firedToday, state.simMin);
  if (slotMatch) {
    const nextFired = new Set(state.firedToday);
    nextFired.add(slotMatch.id);
    const durationMin =
      slotMatch.durMin + rng() * (slotMatch.durMax - slotMatch.durMin);
    return {
      activityId: slotMatch.actId,
      durationMin,
      firedToday: nextFired,
      resolved: resolveAct(slotMatch.actId),
    };
  }

  const hour = Math.floor((state.simMin % 1440) / 60);
  const pool = getActPool(agent, weekend);
  const picked = weightedPickWithRng(
    pool,
    (act) => scoreFloat(act, agent, hour, state.needs, state.lastActId, weekend),
    rng,
  );
  if (!picked) {
    return null;
  }

  const resolved = resolveAct(picked);
  const duration = resolved?.dur || [20, 40];
  return {
    activityId: resolved?.id ?? (typeof picked === 'string' ? picked : picked.id),
    durationMin: duration[0] + rng() * (duration[1] - duration[0]),
    firedToday: new Set(state.firedToday),
    resolved,
  };
}

export function synthesizeBootstrapLifeBackfill({
  agent,
  world,
  lifeSim,
  activityLog,
  nowMs = Date.now(),
  describeActivity = null,
}) {
  if (!agent || !lifeSim) {
    return null;
  }
  if (world?.mode && world.mode !== 'life') {
    return null;
  }

  const catchupStartMs = inferCatchupStartTimestamp(activityLog, nowMs, lifeSim);
  if (!catchupStartMs || catchupStartMs >= nowMs) {
    return null;
  }

  let remainingMin = Math.min(
    MAX_CATCHUP_MINUTES,
    Math.max(0, (nowMs - catchupStartMs) / 60000),
  );
  if (remainingMin < 1) {
    return null;
  }

  const state = {
    needs: lifeSim?.needs || initNeeds(),
    firedToday: Array.isArray(lifeSim?.fired_today) ? new Set(lifeSim.fired_today) : new Set(),
    simMin: Number.isFinite(lifeSim?.sim_min) ? lifeSim.sim_min : 0,
    dayNum: Number.isFinite(lifeSim?.day_num) ? lifeSim.day_num : 0,
    lastActId: typeof lifeSim?.last_act_id === 'string' ? lifeSim.last_act_id : null,
    actEndMin: Number.isFinite(lifeSim?.act_end_min) ? lifeSim.act_end_min : 0,
  };

  normalizeLifeStateForDay(state);

  const rng = createDeterministicRng(
    `${agent.id}|${catchupStartMs}|${state.simMin}|${state.dayNum}|${state.lastActId || 'idle'}`,
  );
  const entries = [];
  let currentPlaceKey = inferCurrentPlaceKey(world, state.lastActId);
  let stateChanged = false;
  let transitions = 0;

  while (remainingMin > 0.01 && transitions < MAX_BACKFILL_TRANSITIONS) {
    normalizeLifeStateForDay(state);

    const currentRemaining = state.lastActId
      ? Math.max(0, (state.actEndMin || 0) - state.simMin)
      : 0;
    if (currentRemaining > 0.01) {
      const advance = Math.min(currentRemaining, remainingMin);
      state.needs = tickNeeds(state.needs, state.lastActId, advance);
      state.simMin += advance;
      remainingMin -= advance;
      stateChanged = stateChanged || advance > 0.01;
      continue;
    }

    const nextActivity = pickNextLifeActivity(agent, state, rng);
    if (!nextActivity?.activityId) {
      break;
    }

    state.lastActId = nextActivity.activityId;
    state.firedToday = nextActivity.firedToday;
    state.actEndMin = state.simMin + nextActivity.durationMin;
    transitions += 1;
    stateChanged = true;

    const transitionTs = Math.max(
      catchupStartMs,
      Math.round(nowMs - remainingMin * 60000),
    );
    const place = describeActivityPlace(agent, state.lastActId);
    if (place.placeKey !== currentPlaceKey) {
      entries.push({
        id: buildHistoricEntryId(agent.id, 'zone_change', place.locationTag, transitionTs),
        timestamp: transitionTs,
        agentId: agent.id,
        type: 'zone_change',
        message: `🚶 Heading to ${place.placeName}`,
        emoji: '🚶',
        meta: {
          bootstrap_backfill: true,
          activityId: state.lastActId,
          placeKey: place.placeKey,
        },
      });
    }

    entries.push({
      id: buildHistoricEntryId(agent.id, 'life_sim', state.lastActId, transitionTs + 1),
      timestamp: transitionTs + 1,
      agentId: agent.id,
      type: 'life_sim',
      message: nextActivity.resolved?.name
        ? `Now ${nextActivity.resolved.name.toLowerCase()}`
        : `Now ${state.lastActId}`,
      emoji: nextActivity.resolved?.emoji || '●',
      meta: {
        bootstrap_backfill: true,
        activityId: state.lastActId,
      },
    });

    currentPlaceKey = place.placeKey;
  }

  normalizeLifeStateForDay(state);

  const finalResolved = state.lastActId ? resolveAct(state.lastActId) : null;
  const finalPlace = state.lastActId
    ? describeActivityPlace(agent, state.lastActId)
    : {
        locationTag: null,
      };
  const finalTaskText = finalResolved
    ? (typeof describeActivity === 'function' ? describeActivity(agent, finalResolved) : null)
    : null;

  if (!stateChanged && entries.length === 0) {
    return null;
  }

  return {
    changed: true,
    lifeSim: {
      needs: state.needs,
      fired_today: Array.from(state.firedToday),
      sim_min: state.simMin,
      day_num: state.dayNum,
      last_act_id: state.lastActId,
      act_end_min: state.actEndMin,
    },
    world: {
      mode: 'life',
      state: 'idle',
      current_task: finalTaskText,
      current_zone_id: world?.current_zone_id || 'life',
      activity: state.lastActId || null,
      location_tag: finalPlace.locationTag || null,
    },
    entries: entries.slice(-MAX_BACKFILL_ENTRIES),
  };
}
